import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import type { SelectOption } from '@/components';
import type { CancelBookingReservationData } from '@/types/api/mutations';

import { BookingDialogBody } from '@/components';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

import { bookingReservationQueries } from '@/queries';

import { useCancelBookingReservationMutation } from '@/mutations';

import { notifyBookingSuccess, notifyMutationError } from '@/helpers';

import { BOOKING_CLOSED_STATUSES } from '@/constants';

import {
  MissedCommentDialog,
  useMissedCommentDialog,
} from '../MissedCommentDialog';

import { AttachReservationForm } from './AttachReservationForm';
import { CancelBookingDialog } from './CancelBookingDialog';
import { CancelBookingReasonDialog } from './CancelBookingReasonDialog';
import { EditReservationForm } from './EditReservationForm';
import { ReservationSummary } from './ReservationSummary';

type ReservationDetailMode = 'view' | 'edit' | 'attach';

export type ReservationDetailProps = {
  reservationId: number | null;
  reservationsUrl: string;
  specialistAvailabilityUrl: string;
  clientsSearchUrl: string;
  clientsUrl: string;
  newClientsUrl: string;
  cashRegistersUrl: string;
  staff: SelectOption[];
  locations: SelectOption[];
  refundReasons: SelectOption[];
  isEditInitially?: boolean;
  onClose: () => void;
  onChanged: () => void;
};

export const ReservationDetail = (props: ReservationDetailProps) => {
  const {
    reservationId,
    reservationsUrl,
    specialistAvailabilityUrl,
    clientsSearchUrl,
    clientsUrl,
    newClientsUrl,
    cashRegistersUrl,
    staff,
    locations,
    refundReasons,
    isEditInitially = false,
    onClose,
    onChanged,
  } = props;
  const { t } = useTranslation();

  const [mode, setMode] = useState<ReservationDetailMode>(
    isEditInitially ? 'edit' : 'view',
  );
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

  const onBack = useCallback(() => setMode('view'), []);

  const {
    data: reservation,
    isLoading,
    refetch,
  } = useQuery(
    bookingReservationQueries.detail({
      url: reservationsUrl,
      id: reservationId,
    }),
  );

  const onReservationChange = useCallback(() => {
    refetch();
    onChanged();
  }, [onChanged, refetch]);

  const missedComment = useMissedCommentDialog({
    reservationsUrl,
    onMarked: () => {
      onChanged();
      onClose();
    },
    onEdited: onReservationChange,
  });
  const { mutate: cancelBooking, isPending: isCancelPending } =
    useCancelBookingReservationMutation();

  // Neither a class spot nor an event registration can be rescheduled: the gathering owns the time.
  const isReschedulable = reservation?.kind === 'personal';
  // An event holds nobody's time, so the default «the trainer's time is freed» wording is wrong.
  const isEvent = reservation?.kind === 'event';

  const onCancelConfirm = useCallback(
    (data: CancelBookingReservationData) => {
      if (!reservationId) {
        return;
      }
      cancelBooking(
        { url: `${reservationsUrl}/${reservationId}`, data },
        {
          onSuccess: (response) => {
            notifyBookingSuccess(response);
            setIsCancelConfirmOpen(false);
            onChanged();
            onClose();
          },
          onError: (error) => {
            notifyMutationError(error);
            refetch();
          },
        },
      );
    },
    [
      cancelBooking,
      onChanged,
      onClose,
      refetch,
      reservationId,
      reservationsUrl,
    ],
  );

  const isClosed =
    !!reservation && BOOKING_CLOSED_STATUSES.includes(reservation.status);
  const isCancelDialogShown = isCancelConfirmOpen && !isClosed;

  const isMissable = reservation?.status === 'reserved';

  const client = reservation?.client;
  const clientCardUrl = client?.is_new
    ? `${newClientsUrl}/${client.id}/edit`
    : `${clientsUrl}/${client?.id}`;

  if (reservation && mode === 'edit') {
    return (
      <EditReservationForm
        reservation={reservation}
        reservationsUrl={reservationsUrl}
        specialistAvailabilityUrl={specialistAvailabilityUrl}
        staff={staff}
        locations={locations}
        onClose={onBack}
        onSuccess={onReservationChange}
      />
    );
  }

  if (reservation && mode === 'attach') {
    return (
      <AttachReservationForm
        reservation={reservation}
        reservationsUrl={reservationsUrl}
        clientsSearchUrl={clientsSearchUrl}
        onClose={onBack}
        onSuccess={onReservationChange}
      />
    );
  }

  return (
    <>
      <BookingDialogBody>
        {isLoading && <Skeleton className="h-64 w-full" />}

        {!isLoading && reservation && (
          <ReservationSummary
            reservation={reservation}
            isMissedCommentPending={missedComment.isPending}
            onMissedCommentEdit={() =>
              missedComment.openEdit(reservation.id, reservation.missed_comment)
            }
          />
        )}
      </BookingDialogBody>

      <DialogFooter className="flex-wrap gap-2 sm:justify-between">
        {reservation && !isClosed && (
          <Button
            variant="destructive"
            onClick={() => setIsCancelConfirmOpen(true)}
            disabled={isCancelPending}
          >
            {t('Cancel booking')}
          </Button>
        )}

        <div className="flex flex-wrap gap-2 sm:ml-auto">
          {reservation?.client && (
            <Button
              variant="outline"
              asChild
            >
              <a href={clientCardUrl}>{t('Go to client card')}</a>
            </Button>
          )}
          {reservation && !isClosed && (
            <>
              {isMissable && (
                <Button
                  variant="outline"
                  onClick={() => missedComment.openMark(reservation.id)}
                  disabled={missedComment.isPending}
                >
                  {t('Mark no-show')}
                </Button>
              )}
              {isReschedulable && (
                <Button
                  variant="outline"
                  onClick={() => setMode('edit')}
                >
                  {t('Edit')}
                </Button>
              )}
              <Button onClick={() => setMode('attach')}>{t('Attach')}</Button>
            </>
          )}
        </div>
      </DialogFooter>

      {missedComment.dialogProps && (
        <MissedCommentDialog {...missedComment.dialogProps} />
      )}

      {isCancelDialogShown && !reservation?.is_purchase_refundable && (
        <CancelBookingReasonDialog
          isPending={isCancelPending}
          title={
            isEvent ? t('Cancel this registration?') : t('Cancel this booking?')
          }
          description={
            isEvent
              ? t('The place is freed.')
              : t('The trainer’s time is freed.')
          }
          onClose={() => setIsCancelConfirmOpen(false)}
          onConfirm={(cancellationReason) =>
            onCancelConfirm({ cancellation_reason: cancellationReason })
          }
        />
      )}

      {isCancelDialogShown && reservation?.is_purchase_refundable && (
        <CancelBookingDialog
          isOpen
          cashRegistersUrl={cashRegistersUrl}
          refundReasons={refundReasons}
          isPending={isCancelPending}
          title={isEvent ? t('Cancel this registration?') : undefined}
          description={
            isEvent
              ? t(
                  'The place is freed. A purchase made for this registration is refunded.',
                )
              : undefined
          }
          onOpenChange={setIsCancelConfirmOpen}
          onConfirm={onCancelConfirm}
        />
      )}
    </>
  );
};
